import { Button, Divider, Typography } from '@mui/material';
import { INCOME_OR_EXPENSE_LIST } from 'adminConstants/routes';
import Page from 'components/new/Page';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { IncomeOrExpenseType } from 'further-types/lp/income-and-expenses';
import { Column, Row } from 'further-ui/components/Layout';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import api from 'lib/trpcClient';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { FormValues } from './FormWrapper';
import OriginalActionSummary from './OriginalActionSummary';

const CreateReversalPage: React.FC<{ vehicleId: string }> = ({ vehicleId }) => {
  const { actionId: groupId } = useParams<{ actionId: string }>();
  const { transactionTypeOptions } = useTransactionTypeSelectorOptions();
  const { vehicle } = useLayoutContext();
  const { control, watch, handleSubmit, getValues } =
    useFormContext<FormValues>();
  const history = useHistory();
  const reversalDate = watch('reversalDate');

  const [actionData] = api.incomeAndExpenses.get.useSuspenseQuery({
    id: groupId ?? '',
    vehicleId,
  });

  const { data: reversalTransactions } =
    api.lpReversal.getIncomeOrExpenseTransactions.useQuery({
      vehicleId,
      groupId: groupId ?? '',
      reversalDate,
    });

  const reverseAction = api.lpReversal.reverseIncomeOrExpense.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Action reversed successfully.',
      onSuccess: () => {
        history.push(INCOME_OR_EXPENSE_LIST);
      },
    }),
  );

  const onSubmit = () => {
    const data = getValues();
    reverseAction.mutate({
      ...data,
      vehicleId,
    });
  };

  const actionLabel =
    actionData?.category === IncomeOrExpenseType.Income ? 'Income' : 'Expense';

  return (
    <Page
      tabTitle={`Reverse ${actionLabel}`}
      spacing="lg"
      heading={`Reverse ${actionLabel}`}
      withHeadingDivider
      goBackAction={INCOME_OR_EXPENSE_LIST}
      actionBar={
        <Row fullWidth centered justify="space-between">
          <Button variant="outlined" onClick={() => history.goBack()}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            loading={reverseAction.isPending}
          >
            Approve Reversal
          </Button>
        </Row>
      }
    >
      <Column spacing="lg">
        <FormField label="Reversal date" size="md">
          <Controller
            name="reversalDate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                fullWidth
                value={field.value ?? null}
                onChange={(date) => {
                  if (date) {
                    field.onChange(date);
                  }
                }}
                error={!!error}
                helperText={error?.message?.toString()}
              />
            )}
          />
        </FormField>

        <Divider />

        <Typography variant="h6">Original action</Typography>

        <OriginalActionSummary vehicleId={vehicleId} groupId={groupId ?? ''} />

        <Divider />

        <Typography variant="h6">Account transactions</Typography>

        {vehicle && (
          <TransactionAllocator
            readOnly
            transactions={reversalTransactions ?? []}
            transactionTypeOptions={transactionTypeOptions}
            transactionCurrency={vehicle.baseCurrency}
            forceShowTcy={(reversalTransactions ?? []).some(
              (t) => t.transactionCurrency !== vehicle.baseCurrency,
            )}
            vehicle={{
              _id: vehicleId,
              baseCurrency: vehicle.baseCurrency,
              fxDataFeed: vehicle.fxDataFeed,
            }}
            fxRate={null}
          />
        )}
      </Column>
    </Page>
  );
};

export default CreateReversalPage;
